import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useSummaryData from "../../hooks/useSummaryData";
import styled from "styled-components";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../../components/loadingContainer";
import ContainerHeader from "../../components/containerHeader";
import SummaryAccordion from "../../components/summaryAccordion";
import useEcomodClasses from "../../hooks/useEcomodClasses";
import { useUIStore } from "../../stores/ui-store";
import { useShallow } from "zustand/shallow";
import { UIState } from "../../types/ui-types";
import useSupClasses from "../../hooks/useSupClasses";
import usePrices from "../../hooks/usePrices";
import {
  getChangeErrors,
  getDivideAmount,
  getMatchingReport,
  getMultiplyAmount,
  getReportingCycle,
} from "../../utils/summaryUtils";
import { Report, ReportStatus } from "../../api/models/report.models";
import TitleComponent from "../../components/title";
import { Button } from "primereact/button";
import useApproveReports from "../../hooks/useApproveReports";
import { Company } from "../../api/models/companies.models";
import GoBackLink from "../../components/goBackLink";

const PaddedContainer = styled.div`
  padding: 2rem 3rem 6rem 3rem;
  text-align: left;
  flex-grow: 1;
`;

const ReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  width: 100%;
  min-height: 95vh;
`;

const StickyActions = styled.div`
  display: flex;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  background-color: #fff;
  z-index: 100;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;

  & .p-button {
    margin-right: 3rem;
  }
`;

const TotalCostContainer = styled.div`
  margin-top: 2rem;
  padding-right: 1rem;
  font-size: 18px;
  font-weight: 600;
  width: 100%;
  text-align: right;
  display: flex;
  flex-direction: column;

  & .notification {
    font-size: 14px;
    color: var(--text-color-secondary);
    font-weight: 500;
    margin-top: 0.5rem;
  }
`;

const CommentSection = styled.div`
  margin-bottom: 2rem;
  padding: 0 1rem;
`;

const CommentContent = styled.div`
  flex: 1;

  & h4 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 0.25rem;
  }
`;

const ChangeErrorContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 3rem;
  padding: 1rem;
  background-color: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;

  & h4 {
    margin-top: 0;
    font-size: 16px;
    color: #d32f2f;
  }

  & ul {
    margin: 0.5rem 0 0 1.5rem;
    padding-left: 0;
    color: #d32f2f;

    & li {
      margin-bottom: 0.25rem;
    }
  }
`;

interface AdminCombinedReportViewProps {
  reportsData?: Report[];
  company: Company;
  year?: number;
  quarter?: number;
  month?: number;
  prevYearReportsData?: Report[];
  hide: () => void;
}

const AdminCombinedReportView: React.FC<AdminCombinedReportViewProps> = ({
  reportsData,
  company,
  year,
  quarter,
  month,
  prevYearReportsData,
  hide,
}) => {
  const { t } = useTranslation();

  const [locale] = useUIStore(useShallow((state: UIState) => [state.locale]));

  const combineReports = (reports?: Report[]): Report | undefined => {
    if (!reports || reports.length === 0) {
      return undefined;
    }

    // TO DO: DO STUFF HERE TO COMBINE REPORTS
    const combinedReport: Report = { ...reports[0], rows: [] };
    reports.forEach((report) => {
      combinedReport.rows.push(...report.rows);
    });
    return combinedReport;
  };

  const reportData = useMemo(() => combineReports(reportsData), [reportsData]);

  const prevYearReport = useMemo(() => {
    if (!reportsData || !prevYearReportsData) return undefined;

    const matchedPrevReports = reportsData
      .map((currentReport) => {
        const orgPrevReports = prevYearReportsData.filter(
          (r) => r.organizationId === currentReport.organizationId,
        );
        return getMatchingReport(orgPrevReports, currentReport);
      })
      .filter((r): r is Report => r !== null);

    if (matchedPrevReports.length === 0) return undefined;
    const combinedReport: Report = { ...matchedPrevReports[0], rows: [] };
    matchedPrevReports.forEach((report) => {
      combinedReport.rows.push(...report.rows);
    });
    return combinedReport;
  }, [reportsData, prevYearReportsData]);

  const { pricesData, isLoading: isLoadingPrices } = usePrices({
    year: year ? year : new Date().getFullYear(),
  });

  const { isApproving, approveAndSendReports } = useApproveReports();

  const { ecomodClasses, isLoading: isLoadingEcomodClasses } =
    useEcomodClasses();

  const { supClasses, isLoading: isLoadingSupClasses } = useSupClasses();

  const {
    consumerData,
    prevYearConsumerData,
    businessData,
    prevYearBusinessData,
    supData,
    prevYearSupData,
    totalCost,
  } = useSummaryData({
    reportData,
    prevYearReport,
    ecomodClasses,
    supClasses,
    pricesData,
    locale,
  });

  const currentYearReportingCycle = getReportingCycle(reportData as Report);

  const prevYearReportingCycle = getReportingCycle(prevYearReport as Report);

  const dividePreviousYearBy = getDivideAmount(
    prevYearReportingCycle,
    currentYearReportingCycle,
  );

  const multiplyPreviousYearBy = getMultiplyAmount(
    prevYearReportingCycle,
    currentYearReportingCycle,
  );

  const changeErrors = useMemo(() => {
    return getChangeErrors(
      consumerData || [],
      dividePreviousYearBy,
      multiplyPreviousYearBy,
      prevYearConsumerData,
      t("label-consumer"),
    )
      .concat(
        getChangeErrors(
          businessData || [],
          dividePreviousYearBy,
          multiplyPreviousYearBy,
          prevYearBusinessData,
          t("label-business"),
        ),
      )
      .concat(
        getChangeErrors(
          supData || [],
          dividePreviousYearBy,
          multiplyPreviousYearBy,
          prevYearSupData,
          t("label-single-use-plastic"),
        ),
      );
  }, [
    consumerData,
    prevYearConsumerData,
    businessData,
    prevYearBusinessData,
    supData,
    prevYearSupData,
    dividePreviousYearBy,
    multiplyPreviousYearBy,
    t,
  ]);

  const reportsWithComments = useMemo(
    () => reportsData?.filter((r) => !!r.comment) ?? [],
    [reportsData],
  );

  const approveAndSend = () => {
    const unsentReports = reportsData?.filter(
      (report) => report.reportStatus === ReportStatus.WaitingForApproval,
    );

    if (unsentReports && unsentReports.length > 0) {
      approveAndSendReports(unsentReports);
    }
  };

  const allSent = reportsData?.every(
    (report) => report.reportStatus === ReportStatus.Sent,
  );

  const isLoading =
    isLoadingEcomodClasses || isLoadingSupClasses || isLoadingPrices;

  return (
    <ReportContainer className="report-container">
      <LoadingContainer
        status={
          isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS
        }
      >
        <div style={{ maxWidth: "200px" }}>
          <GoBackLink
            onClick={() => {
              hide();
            }}
          >
            {`${t("go-back")}` || "← Go back"}
          </GoBackLink>
        </div>

        <TitleComponent
          text={
            t("header-report-summary-of-company", {
              company: company.companyName,
            }) +
            " " +
            year +
            (quarter ? " Q" + quarter : "")
          }
        />

        {reportData?.rows.length === 0 && (
          <PaddedContainer>
            <div style={{ paddingLeft: "2rem" }}>
              {t("text-no-data-in-report")}
            </div>
          </PaddedContainer>
        )}
        <PaddedContainer>
          {reportsWithComments.length > 0 && (
            <CommentSection>
              {reportsWithComments.map((report) => {
                const org = company.organizations?.find(
                  (o) => o.id === report.organizationId,
                );
                return (
                  <CommentContent key={report.id}>
                    {org && <h4>{org.organizationName}</h4>}
                    <p style={{ margin: "0 0 0.5rem" }}>{report.comment}</p>
                    {changeErrors.length > 0 && (
                      <ChangeErrorContainer>
                        <h4>{t("header-changes-detected")}</h4>
                        <ul>
                          {changeErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </ChangeErrorContainer>
                    )}
                  </CommentContent>
                );
              })}
            </CommentSection>
          )}
          {!!consumerData?.length &&
            consumerData.some((row) => row.combinedWeight > 0) && (
              <SummaryAccordion
                tableData={consumerData || []}
                prevYearData={prevYearConsumerData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                header={t("label-consumer")}
              />
            )}
          {!!businessData?.length &&
            businessData.some((row) => row.combinedWeight > 0) && (
              <SummaryAccordion
                tableData={businessData || []}
                prevYearData={prevYearBusinessData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                header={t("label-business")}
              />
            )}
          {!!supData?.length &&
            supData.some((row) => row.combinedWeight > 0) && (
              <SummaryAccordion
                tableData={supData || []}
                prevYearData={prevYearSupData}
                prevYearReportingCycle={prevYearReportingCycle}
                currentYearReportingCycle={currentYearReportingCycle}
                header={t("label-single-use-plastic")}
              />
            )}

          <TotalCostContainer>
            <div>
              {`${t("label-total-cost")}: ${totalCost} ${totalCost ? "€" : ""}`}
              {`${totalCost ? ` ${t("label-vat-amount")}` : ""}`}
            </div>
            <div className="notification">
              {t("label-total-amount-notification")}
            </div>
          </TotalCostContainer>
        </PaddedContainer>

        {!allSent && (
          <StickyActions>
            <Button
              type="button"
              onClick={approveAndSend}
              label={t("cmd-approve-and-send")}
              className="p-ml-2"
              loading={isApproving}
            />
          </StickyActions>
        )}
      </LoadingContainer>
    </ReportContainer>
  );
};

export default AdminCombinedReportView;
